<script setup lang="ts">
import type { ToasterProps } from 'vue-sonner'
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/vue/24/outline'
import { Toaster } from 'vue-sonner'

withDefaults(
  defineProps<{
    position?: ToasterProps['position']
  }>(),
  { position: 'top-left' },
)
</script>

<template>
  <Toaster :position="position" close-button close-button-position="top-right" rich-colors :duration="4000">
    <template #success-icon>
      <CheckCircleIcon class="size-5 text-green-600" />
    </template>
    <template #info-icon>
      <InformationCircleIcon class="size-5 text-accent" />
    </template>
    <template #warning-icon>
      <ExclamationTriangleIcon class="size-5 text-yellow-700" />
    </template>
    <template #error-icon>
      <ExclamationCircleIcon class="size-5 text-error" />
    </template>
  </Toaster>
</template>

<style>
[data-sonner-toaster] {
  font-family: 'DM Sans', ui-sans-serif, system-ui, sans-serif;
}

/* Theme tokens — map vue-sonner's CSS custom properties to forgd palette.
   Palette vars come from libs/ui-kit/src/styles/theme.css `@theme` blocks. */
[data-sonner-toaster][data-sonner-theme='light'] {
  --normal-bg: var(--color-neutral-200);
  --normal-text: var(--color-muted-fg);
  --normal-border: transparent;
  --success-bg: var(--color-green-100);
  --success-text: var(--color-muted-fg);
  --success-border: transparent;
  --info-bg: var(--color-muted-bg);
  --info-text: var(--color-muted-fg);
  --info-border: transparent;
  --warning-bg: var(--color-yellow-100);
  --warning-text: var(--color-muted-fg);
  --warning-border: transparent;
  --error-bg: var(--color-red-100);
  --error-text: var(--color-muted-fg);
  --error-border: transparent;
  --border-radius: 0.5rem;
  --toast-icon-margin-start: 0;
  --toast-icon-margin-end: 0;
  --toast-svg-margin-start: 0;
  --toast-svg-margin-end: 0;
}

/* Pin the close button inside the corner (vue-sonner default translates
   it outside via translate(35%, -35%)). Same selector as vue-sonner's
   top-right rule, later source order wins. */
[data-sonner-toaster] [data-close-button-position='top-right'] {
  --toast-close-button-top: 12px;
  --toast-close-button-right: 12px;
  --toast-close-button-transform: none;
}

/* Layout overrides. Selectors match vue-sonner's own (specificity 0,3,0
   for nested ones, 0,2,0 for the toast itself) — later source order wins,
   so no `!important` required. */
[data-sonner-toast][data-styled='true'] {
  align-items: flex-start;
  gap: 0.75rem;
  padding-right: 3rem;
}

[data-sonner-toast][data-styled='true'] [data-title] {
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 600;
}

[data-sonner-toast][data-styled='true'] [data-description] {
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 400;
}

[data-sonner-toast][data-styled='true'] [data-content] {
  flex: 1 1 0%;
  gap: 0.25rem;
}

[data-sonner-toast][data-styled='true'] [data-icon] {
  width: 1.25rem;
  height: 1.25rem;
}

[data-sonner-toast][data-styled='true'] [data-close-button] {
  height: 1.75rem;
  width: 1.75rem;
  border-radius: 0.375rem;
  background: transparent;
}

[data-sonner-toast][data-styled='true'] [data-close-button] > svg {
  height: 1.25rem;
  width: 1.25rem;
}

[data-sonner-toast][data-styled='true']:hover [data-close-button]:hover {
  background: rgb(0 0 0 / 0.05);
}
</style>
